import type { EncounterSummary } from "@allergy123/contracts/clinical";
import { Button, Card, Field, Status, Table, Td, Th } from "@allergy123/ui";
import { useState } from "react";
import { Link } from "react-router";
import { useClinical, useLoad } from "../api";
import { errorText } from "../lib/errors";
import { formatDateTime, kindLabel } from "../lib/format";
import { useSession } from "../session";

/** Unfinished encounters at the location (Q-22): server-side drafts anyone with write may resume or cancel. */
export function EncountersPage() {
  const s = useSession();
  const api = useClinical();
  const timezone = s.location?.timezone ?? "America/Denver";
  const drafts = useLoad((a) => (s.location ? a.encounters.search({ locationId: s.location.locationId, status: ["draft"], limit: 50 }) : Promise.resolve(null)), [s.location?.locationId]);
  const [cancelling, setCancelling] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  async function cancel(e: EncounterSummary) {
    setBusy(true);
    setNote(null);
    try {
      const detail = await api.encounters.get(e.encounterId);
      await api.encounters.cancel(e.encounterId, { version: detail.encounter.version, reason: reason.trim() || null });
      setCancelling(null);
      setReason("");
      drafts.reload();
    } catch (err) {
      setNote(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  const items = drafts.data?.items ?? [];
  return (
    <>
      <div className="mb-8">
        <h1 className="mb-2 text-h1 font-semibold text-anchor">Unfinished encounters</h1>
        <p className="max-w-[68ch] text-ink-secondary">Drafts started at {s.location?.name ?? "this location"} that were not submitted. Resume one to continue where it stopped, or cancel it with a reason. Nothing is deleted automatically.</p>
      </div>
      {drafts.state === "error" && <Status kind="error" label={`Couldn't load drafts: ${errorText(drafts.error)}`} className="mb-4 block" />}
      {note && <Status kind="error" label={note} className="mb-4 block" />}
      <Card>
        <Table caption={drafts.state === "ready" ? `${drafts.data?.total ?? 0} unfinished` : "Loading…"}>
          <thead>
            <tr>
              <Th>Started</Th>
              <Th>Kind</Th>
              <Th>Patient</Th>
              <Th>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {drafts.state === "ready" && items.length === 0 && (
              <tr>
                <Td colSpan={4} className="text-ink-secondary">
                  Nothing unfinished.
                </Td>
              </tr>
            )}
            {items.map((e) => (
              <tr key={e.encounterId}>
                <Td>{formatDateTime(e.startedAt, timezone)}</Td>
                <Td>{kindLabel(e.kind)}</Td>
                <Td>
                  <Link to={`/patients/${e.patientId}`}>Patient record</Link>
                </Td>
                <Td>
                  {cancelling === e.encounterId ? (
                    <form
                      className="flex flex-wrap items-end gap-2"
                      onSubmit={(ev) => {
                        ev.preventDefault();
                        void cancel(e);
                      }}
                    >
                      <Field label="Reason" value={reason} onChange={(ev) => setReason(ev.target.value)} maxLength={500} />
                      <Button type="submit" variant="secondary" disabled={busy}>
                        Confirm cancel
                      </Button>
                      <Button type="button" variant="secondary" onClick={() => setCancelling(null)}>
                        Keep
                      </Button>
                    </form>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      <Button href={`/encounters/${e.encounterId}`}>Resume</Button>
                      {s.can("clinical:write") && (
                        <Button variant="secondary" onClick={() => setCancelling(e.encounterId)}>
                          Cancel
                        </Button>
                      )}
                    </div>
                  )}
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card>
    </>
  );
}
